import * as React from "react";
import { Card, CardContent } from "../card";
import { cn } from "../utils";
import { SectionTitle } from "./section-title";

/**
 * CardList — THE flush-card recipe (docs/design/VERTICAL-RHYTHM-PRD.md §T3,
 * root cause R8).
 *
 * Before this existed, "a list/table inside a card" was hand-built three times
 * (ListCard, the wrapped DataTable, SettingsSection), each stripping `<Card>`
 * (`py-0 gap-0` + a `p-0` body) and re-adding a bespoke header/footer — with the
 * `px-4 py-2.5` footer pair duplicated verbatim (list-card.tsx / data-table.tsx)
 * and a documented double-padding footgun. CardList defines the recipe ONCE: a
 * flush `<Card>`, the canonical {@link SectionTitle} `variant="card"` header, a
 * `p-0` body, and the single footer bar — so header, rows, and footer all share
 * the px-4 left edge and the recipe can't be assembled wrong.
 *
 * The header is the SectionTitle-shaped slots (`title`/`count`/`subtitle`/
 * `action`/`eyebrow`); pass a `header` node instead when the surface has bespoke
 * chrome that doesn't map onto SectionTitle (the wrapped DataTable). When no
 * `header` and no `title` is given, the header is omitted entirely (a headerless
 * card, e.g. a title-less DataTable).
 */
export interface CardListProps {
  /** Title → the SectionTitle header. Omit (with no `header`) for a headerless card. */
  title?: React.ReactNode;
  count?: number;
  subtitle?: React.ReactNode;
  action?: React.ReactNode;
  /** Tiny kicker above the title (SectionTitle eyebrow). */
  eyebrow?: React.ReactNode;
  /**
   * Bespoke header node — overrides the SectionTitle-shaped props for callers
   * whose chrome doesn't map onto SectionTitle (e.g. an integrated DataTable
   * header). When set, `title`/`count`/… are ignored.
   */
  header?: React.ReactNode;
  /** Footer line (e.g. pagination / totals). Renders the one px-4 py-2.5 bar. */
  footer?: React.ReactNode;
  /** Optional DOM id on the Card (deep-link anchors — SettingsSection). */
  id?: string;
  className?: string;
  /** Extra classes on the flush body (e.g. a max-height scroll region). */
  bodyClassName?: string;
  children: React.ReactNode;
}

export function CardList({
  title,
  count,
  subtitle,
  action,
  eyebrow,
  header,
  footer,
  id,
  className,
  bodyClassName,
  children,
}: CardListProps) {
  const headerNode =
    header ??
    (title != null ? (
      <SectionTitle
        variant="card"
        size="sm"
        title={title}
        count={count}
        subtitle={subtitle}
        action={action}
        eyebrow={eyebrow}
      />
    ) : null);

  return (
    <Card id={id} className={cn("gap-0 overflow-hidden py-0", className)}>
      {headerNode}
      <CardContent className={cn("p-0", bodyClassName)}>{children}</CardContent>
      {footer != null ? (
        <div className="border-t border-border/60 px-[var(--content-gutter)] py-[var(--footer-py)] type-subtext text-muted-foreground">
          {footer}
        </div>
      ) : null}
    </Card>
  );
}
